import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { cache as SWRCache, mutate } from 'swr';
import UserStatus, { NET_USER } from '../../types/UserStatus';
import RailsApiResponse from '../../types/api/RailsApiResponse';
import UserBalances, {
  NullableUserBalances,
} from '../../types/api/user/Balances';
import { clearUserLocalStorage } from '../../utils';
import { getApi } from '../../utils/apiUtils';
import { injectTrackerScript } from '../../utils/uiUtils';

export const fetchUserBalance = createAsyncThunk<
  UserBalances,
  number | undefined
>('user/fetchBalances', async (retryCount: number = 0, thunkAPI: any) => {
  const response = await getApi<RailsApiResponse<UserBalances>>(
    '/restapi/v2/user/balances',
    {
      cache: 'no-cache',
    },
  ).catch((res: RailsApiResponse<null>) => res);

  if (response.Success && response.Data?.withdrawable_balance != null) {
    return response.Data;
  }
  const canRetry = retryCount < 10;
  if (canRetry) {
    setTimeout(() => thunkAPI.dispatch(fetchUserBalance(++retryCount)), 1000);
  }
  return thunkAPI.rejectWithValue(canRetry);
});

const isLogoutMessageModalEnabled =
  window.__config__.componentSettings?.logout?.logoutMessageModalEnabled;

const initialState: UserStatus = {
  logged_in: false,
  loading: true,
  needsSync: true,
  disable_logout_modal: !isLogoutMessageModalEnabled || false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserStatus>) => {
      if (!action.payload.logged_in) {
        SWRCache.clear();
        clearUserLocalStorage();
      }
      if (state.registration_id) {
        return state;
      }
      if (state.logged_in !== action.payload.logged_in) {
        Sentry.addBreadcrumb({
          category: 'user',
          message: `api status different state: ${state.logged_in} api: ${action.payload.logged_in}`,
          level: Sentry.Severity.Log,
        });
        return action.payload;
      }
      if (action.payload.id) {
        Sentry.setUser({
          id: action.payload.id?.toString(),
          email: action.payload.email,
        });
      } else {
        Sentry.configureScope(scope => scope.setUser(null));
      }

      return {
        ...state,
        ...action.payload,
      };
    },
    setBalance: (state, action: PayloadAction<number>) => {
      state.balance = action.payload;
    },
    setBalances: (state, action: PayloadAction<NullableUserBalances>) => {
      if (state.balances) {
        state.balances = {
          ...state.balances,
          ...action.payload,
        };
      }
    },
    setWebsocketConnected: (state, action: PayloadAction<boolean>) => {
      state.websocket_connected = action.payload;
      return state;
    },
    setLogin: (_, action: PayloadAction<NET_USER>) => {
      injectTrackerScript('loggedin', action.payload.PlayerId);
      Sentry.addBreadcrumb({
        category: 'user',
        message: `logged in id: ${action.payload.PlayerId}`,
        level: Sentry.Severity.Log,
      });
      Sentry.setUser({
        id: action.payload.PlayerId.toString(),
        email: action.payload.Email,
      });
      return {
        id: action.payload.PlayerId,
        balance: action.payload.Balance,
        can_play: action.payload.CanPlay,
        logged_in: true,
        loading: false,
        name: action.payload.Login,
        currency: action.payload.currency,
        token: action.payload.token,
        registration_id: action.payload.PlayerLoginRes?.RegistrationId,
        bonus_applied: action.payload.bonus_applied,
        deposit_threshold_amount: action.payload.deposit_threshold_amount,
        deposit_threshold_ann_met: action.payload.deposit_threshold_ann_met,
        disable_logout_modal: !isLogoutMessageModalEnabled || false,
      };
    },
    setRegistered: (_, action: PayloadAction<NET_USER>) => {
      injectTrackerScript('regconfirm', action.payload.PlayerId);
      Sentry.addBreadcrumb({
        category: 'user',
        message: `registered id: ${action.payload.PlayerId}`,
        level: Sentry.Severity.Log,
      });
      return {
        id: action.payload.PlayerId,
        balance: 0,
        logged_in: true,
        loading: true,
        needsSync: true,
        name: action.payload.Login,
        registration_id: action.payload.registration_id,
        deposit_threshold_amounts: action.payload.deposit_threshold_amount,
        bonus_applied: action.payload.bonus_applied,
        disable_logout_modal: !isLogoutMessageModalEnabled || false,
      };
    },
    setPromptSetLimits: (state, action: PayloadAction<boolean>) => {
      state.prompt_set_limits = action.payload;
      return state;
    },
    setDisableLogoutModal: (state, action) => {
      state.disable_logout_modal = action.payload;
      return state;
    },
    setLogout: () => {
      Sentry.addBreadcrumb({
        category: 'user',
        message: `logged out`,
        level: Sentry.Severity.Log,
      });
      Sentry.configureScope(scope => scope.setUser(null));
      clearUserLocalStorage();
      return {
        ...initialState,
        logged_in: false,
        logout: true,
        loading: false,
        needsSync: false,
      };
    },
    removeUserData: () => {
      clearUserLocalStorage();
      return initialState;
    },
    setTwoFactoAuth: (state, action: PayloadAction<boolean>) => {
      state.authentication_enabled = action.payload;
    },
    setDepositThresholdInfo: (state, action: PayloadAction<any>) => {
      const { threshold_amount, anniversary_met } = action.payload;
      state.deposit_threshold_amount = threshold_amount;
      state.deposit_threshold_ann_met = anniversary_met;
    },
    setValidationStatus: (state, action: PayloadAction<any>) => {
      state.validator_status = action.payload;
    },
    setUserActivated: state => {
      state.registration_id = null;
    },
    setBonusAppliedAcknowledged: state => {
      state.bonus_applied = null;
    },
    setAnniversaryMet: (state, action: PayloadAction<boolean>) => {
      state.deposit_threshold_ann_met = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      fetchUserBalance.fulfilled,
      (state, action: PayloadAction<UserBalances>) => {
        state.balances = action.payload;
        state.balance = action.payload.withdrawable_balance;
      },
    );
  },
});

export const {
  setUser,
  removeUserData,
  setBalance,
  setBalances,
  setLogout,
  setDisableLogoutModal,
  setLogin,
  setTwoFactoAuth,
  setRegistered,
  setPromptSetLimits,
  setValidationStatus,
  setUserActivated,
  setBonusAppliedAcknowledged,
  setDepositThresholdInfo,
  setWebsocketConnected,
  setAnniversaryMet,
} = userSlice.actions;

export default userSlice.reducer;
